import {Model} from '@flexlayout'
import {IServerPDFReportJobPayload, PdfCoverPageJobPayload, PDFTemplateJobPayload, QRCodeReportPageJobPayload} from '@modules/pdf'
import {CatalogInstanceChildStore, CatalogStore, ICatalogUserSettings, nav, site, ui, ReportWrapper, StoresInstance} from '@stores'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {guid, removeUndefinedsNested} from '@utils'
import {action, autorun, computed, observable, reaction, runInAction, toJS, trace, when} from 'mobx'
import {_, stringify, lockr} from '@api'
import {deepObserve, IDisposer} from 'mobx-utils'

export const DEBUG_REPORT_SAVE = false && DEBUG

/**
 * vip-report-version
 */
export interface ICatalogReportVersionEs {
  id: string
  /**
   * From vip-reports
   */
  reportId: string

  version: number
  creator: string
  date: Date
  settings: any
}

/**
 * vip-reports
 */
export interface ICatalogReportEs {
  _id?: string
  id: string
  creator: string
  title?: string
  slug?: string
  createdDate: Date
  modifiedDate: Date
  isPublic: boolean
  version: number
  draftedFrom?: { id?: string, version?: number }
  forkedFrom?: { id?: string, version?: number }

  /**
   * Stringified version of full settings
   * {@see ICatalogUserSettings}
   */
  settings: string
}

export const reportSettingFieldsToSerialize = ['lang', 'flexLayout', 'reportPdf'],
  reportSettingsPayloadOmitFields = ['_id', 'originalSettings', 'settings']

/**
 * Store responsible for loading and saving reports
 *
 * YFTS-9208 YFTS-8867
 *
 * # 'Basic' Flow - Once user is logged in
 *
 * Load Recent Reports from ES
 *    Check URL for id or slug parameter
 *      If set - load that specific report {@see ReportsStore.loadCatalogReportFromQueryParamsOrLocalStorage}
 *      If not, either load the most recent report (if one exists) or create a default report {@see ReportsStore.createReport}
 *     Set {@see ReportsStore.currentReport}
 *      - The report contains a full copy of the root flex layout tree stored in JSON with all of the individual tab's catalog settings.
 *    - Initialize flex layout from the global settings change
 *     - The parsed settings from the report are merged with defaults for all flex layout models {@see FlexLayoutStore.autorun_loadLayoutOnceConfigHasLoaded}
 *     - An actual {@see Model} is created in flex via an initial call to {@see FlexLayoutStore._updateLayoutFromJsonModel}
 *    - The user's report is loaded!
 *      - Each catalog tab gets it's own {@see CatalogRootFlexLayoutTab} and {@see CatalogRootFlexLayoutStore}
 *
 *
 -- REMOVED AFTER ANALYSIS
 - Load the stringified json into global catalog settings
 *       The computed {@see CatalogSettingsStore.settingsFromReport} automatically deserializes the report
 *       Then {@see autorun_loadCatalogSettingsFromCurrentReport} sees that change and merges the report with defaults (which may include new settings not present when the report was last saved)
 *       Now the global catalog store's settings should contain the deserialized + defaults from our loaded report
 ---

 * Report is now loaded
 *    - Model Changes - Anytime a change is done via a flex layout action (add/delete tab, resize splitters, toggle on sidebar node, etc) we get a call to {@see FlexLayoutStore.onModelChange}
 *      - These calls are debounced.  For example while dragging the splitter we may be told about dozens of model changes along the way
 *      - Once the debounce window expires, We diff the current model JSON with our current representation (why?)
 *      - If there's a diff we call {@see _updateSettingFromModelJson} which overwrites the global catalog store's {@see catalog.settings.flexLayout}
 *    - Settings change - Anytime the user performs an action which changes our application state but isn't specific to flex layout's model hierarchy.  Things like filtering, grouping, sorting, picking view options, what workspace you're in, etc, etc
 *      - Any part of the application can edit its own catalog settings (which are local) to it's injected StoresInstance
 *      - Changes are detected at the tab level for a given catalog {@see TabNode.autorun_updateFlexNodeCustomSettings} via the computed {@see TabNode.nextSettings}
 *      - The changes are propagated to the underlying tab's attributes
 *      - The global store's settings are updated via {@see autorun_updateEsReportWhenChanged}
 *
 * Saved Report -> Draft Report
 *  - When a pre-existing, saved report is changed, it switches to being in a 'draft' state with a link back to its original
 *
 * {@static Singleton - Should only be a global version}
 */
export class ReportsStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)

    if (!catalog.isGlobalStore) throw new Error(`This class can only be used as a singleton in the global application store`)

    synchronizeStores(() => {
      // autorun(() => {
      //   const {stores: {currentUser: {userSettings}, nav: {queryParams: {id}}}} = catalog
      //
      //   if (userSettings && id) {
      //     userSettings.recentReports = _.uniq([id, ..._.take(userSettings.recentReports ?? [], 4)])
      //   }
      // })

      // autorun(async() => {
      //   const {catalog: {idFor, stores: {reports, reports: {queryParamsForCurrentReport}, users: {current}, authStore: {userIsAuthenticated}}}} = this
      //   trace()
      //   runInAction(() => {
      //   if (current?.id && idFor(reports.currentReport)  {
      //     await Promise.all([reports.loadCatalogReportFromEs()])
      //     this.urlProcessed = true
      //   }
      //   })
      // }, {name: `Load latest catalog setttings from ES`})

      autorun(async () => {
        const {authStore: {user, userIsAuthenticated}, workspaces: {deploymentConfig}, catalog} = this.stores
        if (userIsAuthenticated && user && !_.isEmpty(deploymentConfig) && catalog.initialized) {
          DEBUG_REPORT_SAVE && console.groupCollapsed(`--- ReportsStore - Initializing / Loading Current Report ---`)
          await this.loadRecentReports()
        }
      }, {name: `Load user settings`}),

        autorun(() => {
          if (!this.stores.authStore.userIsAuthenticated) {
            this.reset()
          }
        }, {name: `Reset reports store on logout`})

      this.allowAutoruns = true
    })
  }

  @computed get myReports() {
    const {recentReports} = this
    return recentReports.filter(r => r.isMyReport)
  }

  @computed get publicReports() {
    return this.recentReports.filter(r => r.isPublic)
  }

  @action openDefaultReport = async () => {
    const {defaultReportId} = this
    nav.updateQueryParams({id: defaultReportId, report: undefined})
    await this.loadCatalogReportFromQueryParamsOrLocalStorage()
  }

  @computed get defaultReportId() {
    return this.stores.currentUser.userSettings?.defaultReport
  }

  @computed get lastOpenedReports() {
    const {lastOpenedReportIds, reportById} = this
    return lastOpenedReportIds.map(id => reportById[id]).filter(_.identity)
  }

  @computed get lastOpenedReportIds(): Array<string> {
    return this.stores.currentUser.userSettings?.recentReports ?? []
  }

  _setup_delayedUpdateCurrentReport = autorun(() => {
    const {currentReport} = this
    this._delayedUpdateCurrentReport = _.debounce(async () => {
        const {stores: {authStore: {userIsAuthenticated}, currentUser: {userSettings}, flexLayout}, site: {isWindowFocused}, _loadingReport, justLoadedReport, currentReport} = this
        if (!userIsAuthenticated || (!DEBUG && !isWindowFocused) || _loadingReport || !currentReport || justLoadedReport) return

        if (this.currentReport.id !== this._reportToUpdate) return  // Todo What to do about pending save that we didn't cover?  Should happen before we unload the report

        if (flexLayout.activeOrLastCatalogRoot && !flexLayout.activeOrLastCatalogRoot.hasFinishedFirstLoad) return

        const {catalog: {settingsStore}, currentReportModel, stores: {}} = this

        //if (_.some())
        const settings = _.pick(settingsStore.reportSettings, reportSettingFieldsToSerialize)

        if (!settings?.flexLayout?.model) return

        /*if (_.isEmpty(currentReport?.originalSettings)) {
          currentReport.originalSettings = _.cloneDeep(settings)
        } else */
        if (!currentReportModel?.isInUndo) {
          if (this.justDiscardedDraft) {
            this.justDiscardedDraft = false
            return
          }

          currentReport.saveLocalDraft(JSON.stringify(_.pick(settings, reportSettingFieldsToSerialize)))

          if (!currentReport.isMyReport) {
            return
          }

          DEBUG_REPORT_SAVE && DEBUG && console.debug(`(Throttled) Saving settings to report`, {settings})
          //lockr.set(localStorageKey, settings)
          // if (currentReportModel && currentReportModel.historyIndex != -1) {
          //   const {changeHistory} = currentReportModel
          //   changeHistory.splice(currentReportModel.historyIndex + 1)
          //   changeHistory.push(this.settingsStringified)
          //   currentReportModel.historyIndex = -1
          // }

          await currentReport.updateSettings(settings, null)
        }
      }, currentReport?.isLocalDraft ? 0 : ui.settings.reports.updateSettingsDebounce, // Take a while - you don't want to interrupt a user's interaction (like double click will change active tab for example and we don't want to trigger a ton of server updates)
      currentReport?.isLocalDraft ? undefined : {leading: false, trailing: true})
  })

  _delayedUpdateCurrentReport : Function

  @action reset = () => {
    this.currentReport = null
    this.currentReportModel = null
    this.recentReports = []
  }

  /**
   * Set to true after startup logic
   */
  @observable allowAutoruns = false

  _settingsObserver: IDisposer
  private autorun_markDirtyOnSettingsChange = autorun(() => {
    if (!this.allowAutoruns) return

    const {catalog: {settingsStore: {settings}}} = this

    if (!this._settingsObserver) {
      // trace()
      this._settingsObserver = deepObserve(settings, (change, path) => {
        if (this._loadingReport || this._needsDirtyUpdate || this.justLoadedReport) return

        DEBUG_REPORT_SAVE && console.log(change, path)

        const {catalog: {flexLayout: {activeTab}}} = this

        if (activeTab?.component === 'whiteboard') {
          const {extraData: {whiteboard}} = activeTab
          if (whiteboard?.isInSession || whiteboard?.isInUndo || whiteboard?.appState?.status !== 'idle') return
        }

        if (this.currentReport) {
          this.currentReport.isDirty = true
          this._needsDirtyUpdate = true
          // const copy = _.cloneDeep(settings)
          //flexLayout.onGlobalSettingsUpdate_TrackVersionInReport(copy)

          // console.log(change, path)
          // trace()
          // const diff = jsondiff.diff(settingsFromLocalStorage, toJS(settings, {recurseEverything: true}))
          // if (diff) {
          // console.log({diff, settings, settingsFromLocalStorage })
          // }
        }
      })
    }
  }, {
    name: `Update global settings on change`
    //delay: 100 // Otherwise we'll do a diff/clone as soon as a user changes anything and cause lag
  })

  @observable _needsDirtyUpdate = false

  /**
   * The report could be pending a save and then the user switches out
   */
  @observable _reportToUpdate : string
  private autorun_updateEsReportWhenChanged = autorun(() => {
    const {allowAutoruns, _needsDirtyUpdate, site} = this
    if (!allowAutoruns || !this.currentReport || !_needsDirtyUpdate || (!DEBUG && !site?.isWindowFocused)) return
    if (StoresInstance.anyStoresNotInitialized) return

    this._needsDirtyUpdate = false
    this._reportToUpdate = this.currentReport.id
    this._delayedUpdateCurrentReport()
  }, {name: `autorun_updateEsReportWhenChanged`})
  @observable recentReports: Array<ReportWrapper> = []
  @observable recentReportsMax = 100

  @computed get reportUserId() {
    const {stores: {users: {current}}} = this
    //if (DEBUG) return `${current?.id} - ${BUILD_USER}`

    return current?.id
  }

  get es() { return this.catalog.stores.es}

  get stores() { return this.catalog.stores}

  @action createReport = async (navigateTo = true, existingReport?: ReportWrapper) => {
    if (!this.hasLoadedRecentReports) return

    const {es, catalog, catalog: {settingsStore}, reportUserId, flexLayout} = this,
      date = new Date()

    DEBUG_REPORT_SAVE && console.debug(`----------- Creating new report --------------`)
    try {
      this.draftedFromId = null
      this.forkedFromReport = null

      const reportJson = {
        settings: stringify(settingsStore.defaultSettings),
        creator: reportUserId,
        createdDate: date,
        modifiedDate: date,
        isPublic: false,
        title: 'New Report',
        version: 1,
        ...(existingReport?.reportJsonForCopy ?? {})
      } as Partial<ICatalogReportEs>

      const newSettings = await es.genericUpsert<ICatalogReportEs & { id: string }>({
        index: es.indexes.reports,
        body: reportJson
      })

      return runInAction(() => {
        const newReport = new ReportWrapper(newSettings.data)
        this.currentReport = newReport
        this.currentReportModel = null
        DEBUG_REPORT_SAVE && console.log(`Created new report`, {newSettings, currentReport: toJS(this.currentReport)})
        if (navigateTo) {
          flexLayout.reset()
          this.recentReports.splice(0, 0, this.currentReport)
          this.updateReportQueryParams()
        }

        return this.currentReport
      })
    } catch (err) {
      catalog.onError(err)
    }
  }

  /**
   * Avoid reloading layout when we auto-fork a report
   */
  @observable.ref forkedFromReport: ICatalogReportEs

  @observable currentReport: ReportWrapper

  /**
   * The most current version of the model for the loaded report
   * This is used when switching to a draft report after a user action to avoid reloading the model due to the changes to the current report
   */
  @observable.ref currentReportModel: Model

  reportById = observable.map<string, ReportWrapper>({}, {deep: false})

  getReport = async ({id, report}: { id?: string, report?: string }): Promise<ReportWrapper> => {
    const {es} = this
    if (id) {
      DEBUG_REPORT_SAVE && DEBUG && console.debug(`Loading report ${id} from ES`)
      this.reportById.set(id, null) // Mark that we're loading
      const r = await es.get<ICatalogReportEs>({index: es.indexes.reports, id: id, ignoreError: true}, {log: {query: false}})
      if (!r) {
        console.error(`Unable to find report id ${id} in ES`)
      }
      return r ? new ReportWrapper({_id: id, ...r}) : null
    } else if (report) {
      const {items} = es.unwrapHitsResult<ICatalogReportEs>(
        await es.search({
          meta: `Get report by custom URL - ${report}`,
          index: es.indexes.reports, body: {
            size: 1,
            query: {match: {slug: report}}
          }
        }, {log: {query: false}}))

      const r = _.first(items)
      return r ? new ReportWrapper({_id: id, ...r}) : null
    } else {
      return null
    }
  }

  @observable draftedFromId: string
  @observable justDiscardedDraft = false

  _loadingReport = false

  justLoadedReport = false
  clearJustLoadedReportHandle: number

  @action loadCatalogReportFromQueryParamsOrLocalStorage = async (openAsPublicReport?: boolean) => {
    const {catalog: {settingsStore}, getReport, stores, stores: {currentUser: {userSettings}, nav: {dashboardQueryParams}}, lastOpenedReportIds} = this
    const id = dashboardQueryParams?.report ? null : (dashboardQueryParams?.id ?? this.defaultReportId ?? _.last(lastOpenedReportIds)),
      report = dashboardQueryParams?.report

    DEBUG_REPORT_SAVE && console.debug(`Load catalog report from ES based on query params / local storage default`, {id, report})

    this.clearJustLoadedReportHandle && window.clearTimeout(this.clearJustLoadedReportHandle)
    this._loadingReport = true
    this.forkedFromReport = null
    this.draftedFromId = null
    this.clearJustLoadedReportHandle = null

    try {
      StoresInstance.disposeAllButGlobal()

      if (openAsPublicReport) {
        userSettings.autoSaveReports = false
      }

      if (id || report) {
        // Get the current version for this known setting ID
        const r = await getReport({id, report})
        if (!r) {
          site.onError(new Error(`We were unable to locate the report - creating a fresh one...\nApologies for the inconvenience.`))
          return null //await this.createDefaultReport()
        } else {
          return runInAction(() => {
            if (!this.currentReport || openAsPublicReport) {
              // Check if we already have a draft version in local storage

              const draftReportSettings = lockr.get<string>(r.localStorageDraftId)
              if (draftReportSettings) {
                r.report.settings = draftReportSettings
                r.isLocalDraft = true
                // const draftSettings = JSON.parse(draftVersion)
                // console.log({original: r.report.settings, draftSettings, [r.localStorageDraftId]: draftVersion})
                // r.report.settings = draftSettings
              }

              r.saveLocalDraft()
            }
            this.currentReport = r
            this.currentReportModel = null
            return this.currentReport
          })
        }
      } else {
        // Get the last settings they used - in the future we'd probably show a homepage with recently used items
        const {recentReports: items} = this
        await runInAction(async () => {
          if (_.isEmpty(items)) {
            // If no settings exist, create defaults and assign an id
            //await this.createDefaultReport()
          } else {
            const [report] = items,
              // Make sure to keep existing setting fields, otherwise we'll break the UI as we'll lose are global defaults
              reportSettings = _.assign(toJS(settingsStore.settings), JSON.parse(report.report.settings))

            settingsStore.settings = reportSettings

            this.currentReport = new ReportWrapper(report.report, reportSettings)
            this.currentReportModel = null
          }
        })
      }
    } finally {

      runInAction(() => {
        this.updateReportQueryParams()
        this.justDiscardedDraft = false
        this._loadingReport = false
        this.justLoadedReport = true
        this.clearJustLoadedReportHandle = window.setTimeout(() => this.justLoadedReport = false, ui.settings.reports.clearJustLoadedReportTimeout)
      })

      // runInAction(`Save user settings from ES`, () => {
      //   _.merge(this.settings, {
      //     workspaces: {},
      //     ...(r?.settings ? JSON.parse(r.settings) : {})
      //   })
      //   this.originalSettings = _.cloneDeep(this.settings)
      //   this._hasLoadedSettingsFromEs = true
      // })
    }
  }

  @action updateReportQueryParams = () => {
    if (this.currentReport) {
      // DEBUG && console.debug(`Navigating to `, toJS({qp: this.queryParamsForCurrentReport}))
      nav.updateQueryParams(this.currentReport.queryParams)
    }
  }

  @observable hasLoadedRecentReports = false
  @action loadRecentReports = async () => {
    const {es, catalog: {idFor, onError}, recentReportsMax, reportUserId, stores: {ui: {settings: {reports: {draftReports}}}}} = this
    try {
      const {items} = es.unwrapHitsResult<ICatalogReportEs>(await es.search(
        removeUndefinedsNested({
          index: es.indexes.reports, meta: `Get the user's last ${recentReportsMax} used catalog settings / report`,
          body: {
            size: recentReportsMax * (draftReports ? 2 : 1), // each item could have a draft as well
            query: {
              bool: {
                //filter: { bool: { minimum_should_match: 1, should: [{term: { isDraft: false }}, { bool: { must_not: { exists: { field: 'isDraft'}}}}]}},
                minimum_should_match: 1,
                should: [
                  {bool: {filter: [{term: {isPublic: true}}, draftReports ? {bool: {must_not: {exists: {field: 'draftedFrom.id'}}}} : null].filter(_.identity)}},
                  {term: {'creator.keyword': reportUserId}}
                ].filter(_.identity),
                filter: draftReports ? undefined : [{bool: {must_not: {exists: {field: 'draftedFrom.id'}}}}]
              }
            },
            // Todo - ideally this would be ordered by our 'last opened' date - we'd need another index in ES for it
            sort: [{'title.keyword': {order: 'asc'}}, {modifiedDate: {order: 'desc', unmapped_type: 'date'}}]
          }
        }), {log: {query: false}}))

      runInAction(async () => {
        this.recentReports = items.map(r => new ReportWrapper(r)).filter(r => !r.isDraft || r.isMyReport || r.isPublic)
        this.hasLoadedRecentReports = true

        DEBUG_REPORT_SAVE && DEBUG && console.debug(`Loaded recent reports:`, items)

        const {dashboardQueryParams: queryParams} = nav
        if (!this.currentReport && !queryParams.report && !queryParams.id) {
          const {defaultReportId, lastOpenedReportIds} = this
          if (defaultReportId || !_.isEmpty(lastOpenedReportIds)) {
            await this.loadCatalogReportFromQueryParamsOrLocalStorage()
          } else if (!_.isEmpty(items)) {
            DEBUG_REPORT_SAVE && console.debug(`No report is specified via the URL but we have recent reports - loading most recent report...`)
            const r = _.first(items)
            this.currentReport = new ReportWrapper(r)
            this.updateReportQueryParams()
          }
        }/* Use the standard loading logic
        else if (!_.isEmpty(items)) {
          let r = items.find(r => (r.slug && r.slug === queryParams.report && !r.draftedFrom) || idFor(r) === queryParams.id)
          if (r) {
            DEBUG_REPORT_SAVE && console.debug(`Report from URL found in recent reports list, no need to load directly from /_doc`)
            this.currentReport = new ReportWrapper(r)
          }
        }*/

        if (!this.currentReport) {
          if (!queryParams.report && !queryParams.id) {
            DEBUG_REPORT_SAVE && console.debug(`No report found on URL and no recent reports, creating default report...`)
            await this.createReport()
          } else {
            const r = await this.loadCatalogReportFromQueryParamsOrLocalStorage()
            if (!r) {
              await this.createReport()
            }
          }

          this.currentReportModel = null
        }

        const {currentReport} = this

        if (ui.settings.reports.draftReports) {
          this.currentReportModel = null
        }

        if (currentReport) {
          DEBUG_REPORT_SAVE && console.debug(`Opened/Loaded ${currentReport.isDraft ? 'Draft ' : ''}Report '${currentReport.displayName}'`, toJS(currentReport))
        }
      })
    } catch (err) {
      onError(err)
    } finally {
      this.hasLoadedRecentReports = true
    }
  }

  @computed get settingsStringified() {
    const {catalog: {settingsStore}} = this
    return stringify(removeUndefinedsNested(_.cloneDeep(_.pick(settingsStore.settings, reportSettingFieldsToSerialize))))
  }

  @computed get isSaveAsDialogOpen() {
    return this.saveAsDialogReport != null
  }

  @action cancelReportDialog = () => {
    const {currentReport, isSaveAsDialogOpen} = this

    if (isSaveAsDialogOpen) {
      this.saveAsDialogReport = null
    } else {
      currentReport?.onStopEditing?.()
    }
  }

  /// region Save As... flow

  @observable saveAsDialogReport: { report: ICatalogReportEs, isSaving?: boolean } = null

  @action onSaveAs_start = () => {
    const {currentReport: {report, isMyReport}, stores: {users: {user}}} = this
    this.saveAsDialogReport = _.cloneDeep({
      report: {
        ...report,
        title: `${report.title ?? 'Report'}${!isMyReport ? ` - ${user.name}` : ''}`,
        slug: null, // otherwise there will be a conflict
        version: 1,
        isPublic: false
      } as ICatalogReportEs
    })
  }

  @action onSaveAs_cancel = () => {
    this.saveAsDialogReport = null
  }

  @action onSaveAs_commit = async (reportContents: Partial<ICatalogReportEs>) => {
    const {currentReport, saveAsDialogReport} = this

    saveAsDialogReport.isSaving = true
    const newReport = await currentReport.cloneReport(true, _.pick(reportContents, ['title', 'slug']))
    this.saveAsDialogReport = null
  }

  /// endregion

  @observable justPublishedDraft = false

  @action onReportNavigatedTo = (report: ReportWrapper) => {
    const {stores: {currentUser: {userSettings}}} = this
    userSettings.recentReports = _.uniq([...(userSettings.recentReports ?? []), report.id])
  }

  /// region Report -> PDF

  @computed get defaultPDFFilename() {
    return this.currentReport?.report?.title ?? this.strings.noValue
  }

  @computed get pdfTemplatesFromReportTabs() : Array<PDFTemplateJobPayload> {
    const {flexLayout: {model, tabsInOrder}} = this

    return tabsInOrder.map(v => v.storesToInject?.cloudPDF?.pdfJobForThisTab).filter(_.identity)
  }

  @computed get currentReportPDFPayload(): IServerPDFReportJobPayload {
    const {currentReport: r} = this
    if (!r) return null

    const {store: {reportUserId}, settingsParsed: {reportPdf}} = r,
      {pdfTemplatesFromReportTabs} = this

    return {
      filename: reportPdf?.filename ?? this.defaultPDFFilename,
      userId: reportUserId,
      ..._.pick(reportPdf, ['title', 'subtitle', 'pageSize', 'orientation']),
      templates: [
        reportPdf?.includeCoverPage ? { template: 'coverPage', title: r.displayName } as PdfCoverPageJobPayload : null,
        reportPdf?.includeQRCode ? { template: 'qrcode-report', reportId: r.id } as QRCodeReportPageJobPayload : null,
        ...pdfTemplatesFromReportTabs
      ].filter(_.identity)
    }
  }

  /// endregion
}

export const defaultReport: Partial<ICatalogReportEs> = {
  draftedFrom: {},
  forkedFrom: {},
  isPublic: false
}





// WEBPACK FOOTER //
// ./src/stores/reports/ReportsStore.ts